import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Edit2, Trash2, Save, X, Upload, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const PartnersPanel = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  const emptyForm = {
    name: '',
    logo: '',
    website: '',
    order: 0,
    is_active: true
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/partners`);
      const data = await response.json();
      setPartners(data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData(emptyForm);
    setIsAdding(true);
    setEditingId(null);
  };

  const handleEdit = (partner) => {
    setFormData(partner);
    setEditingId(partner.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setIsAdding(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      // Convert to base64 data URL for simplicity
      // In production, you'd upload to a cloud storage service
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, logo: reader.result });
        setUploading(false);
        toast.success('Logo uploaded successfully');
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.logo) {
      toast.error('Please fill in name and upload a logo');
      return;
    }

    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/partners/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/partners`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Partner updated!' : 'Partner added!');
      loadPartners();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/partners/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Partner deleted!');
      loadPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/partners/${partner.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !partner.is_active })
      });

      if (!response.ok) throw new Error('Failed to update');

      toast.success(partner.is_active ? 'Partner hidden' : 'Partner visible');
      loadPartners();
    } catch (error) {
      toast.error('Failed to update partner');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Technology Partners</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Manage partner logos displayed on the homepage
            </p>
          </div>
          {!isAdding && !editingId && (
            <Button
              onClick={handleAdd}
              className="bg-[#320547] hover:bg-[#95228A]"
              data-testid="add-partner-btn"
            >
              <Plus className="mr-2" size={20} />
              Add Partner
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {(isAdding || editingId) && (
          <div className="mb-6 p-6 border rounded-2xl bg-white">
            <h3 className="text-lg font-semibold mb-4">
              {editingId ? 'Edit Partner' : 'Add New Partner'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Partner Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Microsoft Azure"
                  required
                  data-testid="partner-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Logo *</label>
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#320547] transition-colors">
                      <Upload size={20} className="text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploading ? 'Uploading...' : 'Upload Logo Image'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="partner-logo-upload"
                      />
                    </label>
                    <span className="text-sm text-gray-400">or</span>
                  </div>
                  
                  {/* URL Input */}
                  <Input
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    placeholder="Paste logo URL here"
                    data-testid="partner-logo-url"
                  />
                  
                  {/* Preview */}
                  {formData.logo && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Preview:</p>
                      <img 
                        src={formData.logo} 
                        alt="Logo preview" 
                        className="h-16 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          toast.error('Invalid logo URL');
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Website URL (optional)</label>
                <Input
                  value={formData.website || ''}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://partner-website.com"
                  data-testid="partner-website-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    data-testid="partner-order-input"
                  />
                </div>
                <div className="flex items-center pt-8">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2 h-4 w-4"
                    data-testid="partner-active-checkbox"
                  />
                  <label className="text-sm font-medium">Show on website</label>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button onClick={handleCancel} variant="outline">
                  <X className="mr-2" size={20} />
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  className="bg-[#320547] hover:bg-[#95228A]"
                  data-testid="save-partner-btn"
                >
                  <Save className="mr-2" size={20} />
                  Save Partner
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((partner) => (
            <Card key={partner.id} className={`${!partner.is_active ? 'opacity-50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center">
                  <div className="w-full h-24 flex items-center justify-center bg-gray-50 rounded-lg mb-3">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  <h4 className="font-bold text-lg">{partner.name}</h4>
                  {partner.website && (
                    <a 
                      href={partner.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-[#320547] hover:underline flex items-center gap-1 mt-1"
                    >
                      <ExternalLink size={12} />
                      Visit website
                    </a>
                  )}
                  <div className="flex items-center gap-1 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${partner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {partner.is_active ? 'Visible' : 'Hidden'}
                    </span>
                    <span className="text-xs text-gray-400">Order: {partner.order}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleToggleActive(partner)}
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs"
                  >
                    {partner.is_active ? 'Hide' : 'Show'}
                  </Button>
                  <Button
                    onClick={() => handleEdit(partner)}
                    size="sm"
                    variant="outline"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    onClick={() => handleDelete(partner.id)}
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {partners.length === 0 && !isAdding && (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-2">No partners yet.</p>
            <p className="text-sm">Click "Add Partner" to add your technology partner logos.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PartnersPanel;
